import { useState } from 'react';
import { Alert, Button, Group, Text } from '@mantine/core';
import { IconTicketOff } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/auth';

const DISMISS_KEY = 'service-report-status-setup-dismissed';

/**
 * Admin-Hinweis: Im Servicebericht-Setup fehlen die Weclapp-Status-IDs.
 *
 * Ohne sie schreibt der Abschluss eines mitbearbeiteten Tickets zwar den Kommentar,
 * setzt aber keinen Status — das Ticket bleibt offen liegen, obwohl es erledigt ist.
 * Der Fehler ist benannt, fällt aber erst beim Absenden auf, also mitten in der Arbeit
 * eines Technikers, der die Einstellung gar nicht ändern darf.
 *
 * Deshalb hier, vorher, für die, die es beheben können. Pro Browser-Session wegklickbar;
 * gepflegt verschwindet er von selbst.
 */
export function ServiceReportStatusSetupBanner() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const canSetup = user?.role === 'ADMIN' || user?.role === 'TEAMLEAD';
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === '1'
  );

  const { data } = useQuery({
    queryKey: ['service-report-status-setup'],
    queryFn: () => api.getServiceReportStatusSetup(),
    enabled: canSetup && !dismissed,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (!canSetup || dismissed || !data) return null;
  if (!data.applies) return null;
  if (data.resolvedConfigured && data.inProgressConfigured) return null;

  const missing = [
    data.resolvedConfigured ? null : '„Erledigt"',
    data.inProgressConfigured ? null : '„In Arbeit"',
  ].filter(Boolean);

  return (
    <Alert
      color="orange"
      radius="md"
      mb="md"
      icon={<IconTicketOff size={18} />}
      withCloseButton
      onClose={() => {
        sessionStorage.setItem(DISMISS_KEY, '1');
        setDismissed(true);
      }}
      title={`Servicebericht: Weclapp-Status für ${missing.join(' und ')} fehlt`}
    >
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Text size="sm" maw={620}>
          Beim Absenden eines Serviceberichts bekommen mitbearbeitete Tickets zwar ihren
          Kommentar, aber keinen Status — sie bleiben offen liegen, obwohl sie erledigt
          sind. Der Techniker sieht dabei einen Fehler, ändern kann er die Einstellung nicht.
          {data.stuckTickets > 0 && (
            <>
              {' '}
              <b>
                {data.stuckTickets === 1
                  ? 'Ein Ticket hängt bereits daran'
                  : `${data.stuckTickets} Tickets hängen bereits daran`}
              </b>{' '}
              und muss{data.stuckTickets === 1 ? '' : 'en'} nach dem Einrichten von Hand
              nachgezogen werden.
            </>
          )}
        </Text>
        <Button size="xs" color="orange" onClick={() => navigate('/admin/settings?tab=weclapp')}>
          Status-IDs hinterlegen
        </Button>
      </Group>
    </Alert>
  );
}
